import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';
import { ZoomDialog } from './zoom-dialog.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
// Reset zoom state when switching slides / closing dialog.
// Matches Freepeople-style behavior: start at 1x.
const RESET_ZOOM = 1;
// Target zoom for (double-tap / double-click) when toggling from 1x.
const DOUBLE_TAP_ZOOM = 1.5;
// Fixed “loupe” zoom strength (mobile press-drag + desktop hover in zoom dialog).
const LOUPE_PEEK_ZOOM = 2.25;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();
  /** @type {number} */
  #scale = RESET_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = RESET_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /** @type {number | null} */
  #zoomIndex = null;

  /** @type {number | null} */
  #lastSlideIndex = null;

  /** @type {boolean} */
  #isPointerDragging = false;

  /** @type {number | null} */
  #pointerId = null;

  /** @type {Point} */
  #pointerStartPosition = { x: 0, y: 0 };

  /** @type {Point} */
  #pointerStartTranslate = { x: 0, y: 0 };

  /** @type {boolean} */
  #hasPointerDraggedBeyondThreshold = false;

  /** @type {Point} */
  #startMidpoint = { x: 0, y: 0 };

  /** @type {Point} */
  #startTwoFingerTranslate = { x: 0, y: 0 };

  /** @type {ReturnType<typeof setTimeout> | null} */
  #mobilePeekUiClearTimer = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #desktopLoupeUiClearTimer = null;

  /** @type {boolean} */
  #desktopLoupeHovering = false;

  /** Cached wrapper bounds for loupe aim (avoid getBoundingClientRect every move). */
  #loupeBounds = { left: 0, top: 0, width: 0, height: 0, valid: false };

  /** @type {number | null} */
  #loupeAimRaf = null;

  /** @type {number} */
  #loupeAimPendingX = 0;

  /** @type {number} */
  #loupeAimPendingY = 0;

  /** @type {boolean} */
  #mobileDragAttrApplied = false;

  /** @type {boolean} */
  #desktopLoupeMovingAttrApplied = false;

  /** @type {HTMLImageElement | null} */
  #imgRef = null;

  connectedCallback() {
    this.#imgRef = this.querySelector('img');
    if (!this.#imgRef) return;

    this.#initResizeListener();
    this.#setupDialogCloseListener();

    const dialogComponent = this.closest('dialog-component[data-zoom-dialog]');
    this.#zoomIndex = Number.parseInt(this.dataset.zoomIndex ?? '', 10);

    // Reset zoom when the zoom dialog switches slides.
    dialogComponent?.addEventListener(
      'zoom-media:selected',
      this.#handleZoomMediaSelected,
      { signal: this.#controller.signal }
    );

    // Prefer slideshow select event (fires immediately on arrows/drag selection).
    dialogComponent?.addEventListener(
      'slideshow:select',
      this.#handleSlideshowSelected,
      { signal: this.#controller.signal }
    );

    this.#initEventListeners();
    this.#refreshLoupeBounds();
    this.#requestUpdateTransform();
  }

  #cancelLoupeAimRaf() {
    if (this.#loupeAimRaf !== null) {
      cancelAnimationFrame(this.#loupeAimRaf);
      this.#loupeAimRaf = null;
    }
  }

  /** Snapshot wrapper layout for loupe math (call on resize / gesture start). */
  #refreshLoupeBounds() {
    const r = this.getBoundingClientRect();
    this.#loupeBounds.left = r.left;
    this.#loupeBounds.top = r.top;
    this.#loupeBounds.width = r.width;
    this.#loupeBounds.height = r.height;
    this.#loupeBounds.valid = r.width > 0 && r.height > 0;
  }

  /**
   * Coalesce high-frequency aim updates to one layout pass per animation frame.
   * @param {number} clientX
   * @param {number} clientY
   */
  #scheduleLoupeAim(clientX, clientY) {
    this.#loupeAimPendingX = clientX;
    this.#loupeAimPendingY = clientY;
    if (this.#loupeAimRaf !== null) return;
    this.#loupeAimRaf = requestAnimationFrame(() => {
      this.#loupeAimRaf = null;
      this.#aimLoupeAtClientPoint(this.#loupeAimPendingX, this.#loupeAimPendingY);
      this.#flushTransformSync();
    });
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  /** @returns {boolean} */
  #isZoomDialogOpen() {
    const dialogComponent = this.closest('dialog-component[data-zoom-dialog]');
    const dialog = dialogComponent?.querySelector('dialog');
    return Boolean(dialog?.open) || document.body.hasAttribute('data-zoom-dialog-open');
  }

  /**
   * Override parent zoom dialog's close method to include reset functionality
   */
  #setupDialogCloseListener() {
    // Find the parent zoom dialog component
    const zoomDialog = /** @type {ZoomDialog} */ (
      /** @type {unknown} */ (this.closest('dialog-component[data-zoom-dialog]'))
    );
    if (!zoomDialog || typeof zoomDialog.close !== 'function') return;

    // Store reference to original close method
    const originalClose = zoomDialog.close.bind(zoomDialog);

    // Override the close method to include zoom reset
    zoomDialog.close = async (...args) => {
      // Reset zoom state before closing
      this.#resetZoom();

      // Call original close method
      return await originalClose(...args);
    };
  }

  /**
   * @param {CustomEvent<{index:number}>} event
   */
  #handleZoomMediaSelected = (event) => {
    // Fallback: zoom-media:selected can lag due to debounce/visibility calculation.
    const activeIndex = event?.detail?.index;
    if (typeof activeIndex !== 'number') return;

    if (this.#lastSlideIndex === activeIndex) return;
    this.#lastSlideIndex = activeIndex;

    // Reset all zoom states in the dialog to ensure no stale pan/zoom persists.
    this.#resetZoom();
  };

  /**
   * @param {CustomEvent<{index:number}>} event
   */
  #handleSlideshowSelected = (event) => {
    const activeIndex = event?.detail?.index;
    if (typeof activeIndex !== 'number') return;

    if (this.#lastSlideIndex === activeIndex) return;
    this.#lastSlideIndex = activeIndex;

    // Reset all zoom states in the dialog to ensure no stale pan/zoom persists.
    // (Even if the user returns to a previous slide immediately.)
    this.#resetZoom();
  };

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    // Touch interactions (pinch/double-tap/drag)
    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);
    this.addEventListener('touchcancel', this.#handleTouchEnd, options);

    // Mouse/pen interactions (drag/zoom)
    this.addEventListener('pointerdown', this.#handlePointerDown, options);
    this.addEventListener('pointermove', this.#handlePointerMove, options);
    this.addEventListener('pointerup', this.#handlePointerUp, options);
    this.addEventListener('pointercancel', this.#handlePointerUp, options);

    // Wheel zoom: mobile / narrow only (desktop uses hover loupe)
    this.addEventListener('wheel', this.#handleWheel, options);

    // Desktop: hover loupe (same focal math as mobile; leave restores 1x)
    const passive = { passive: true, signal };
    this.addEventListener('pointerenter', this.#handleDesktopLoupePointerEnter, passive);
    this.addEventListener('pointermove', this.#handleDesktopLoupePointerMove, passive);
    this.addEventListener('pointerleave', this.#handleDesktopLoupePointerLeave, passive);
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
    this.#clearLoupeUi();
  }

  #handleResize = () => {
    if (this.#initialized) {
      this.#refreshLoupeBounds();
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      if (isMobileBreakpoint()) {
        // Mobile: single-finger press + drag is the zoom model; ignore pinch.
        return;
      }
      // Early exit if touches are invalid
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      // Avoid object allocation by passing touches directly
      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      // Use performance.now() for better precision and performance
      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;

      // Double-tap within time + distance window toggles zoom (before other handling).
      if (
        timeSinceLastTap < DOUBLE_TAP_DELAY &&
        this.#lastTapPosition &&
        getDistance(touch, this.#lastTapPosition) < DOUBLE_TAP_DISTANCE
      ) {
        this.#handleDoubleTapFromTouch(touch);
        this.#lastTapTime = 0; // Reset to prevent triple-tap
        this.#lastTapPosition = null;
        return;
      }

      this.#clearLoupeUi();

      this.#storeTapInfo(currentTime, touch);

      // Mobile: press = loupe at that spot on the base image; moving finger re-aims (not panning a sheet).
      if (isMobileBreakpoint() && Math.abs(this.#scale - MIN_ZOOM) < 0.05) {
        this.#refreshLoupeBounds();
        this.#aimLoupeAtClientPoint(touch.clientX, touch.clientY);
        this.#hasManualZoom = true;
        this.setAttribute('data-mobile-zoom-active', '');
        this.#requestUpdateTransform();
      }

      this.#startDragGestureFromTouch(touch);
    }
  };

  /**
   * Start a zoom gesture with two touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    // Calculate initial distance between touches
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;

    // Store midpoint and start translation so we can "two-finger drag" (pan)
    // when the distance change is negligible.
    this.#startMidpoint = {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
    this.#startTwoFingerTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = false;
  }

  /**
   * Start a drag gesture with a single touch
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * Store tap information for double-tap detection
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Handle double-tap zoom toggle from touch
   * @param {Touch} touch - The touch where the double-tap occurred
   */
  #handleDoubleTapFromTouch(touch) {
    let targetZoom;

    // If manual zoom has been used, reset to 1x
    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM; // 1x
      this.#hasManualZoom = false; // Reset the flag
      this.#translate = { x: 0, y: 0 }; // Center the image
    } else {
      // Toggle between zoom levels: 1x ↔ configured peek zoom
      const tolerance = 0.05; // Small tolerance for floating point comparison

      if (Math.abs(this.#scale - MIN_ZOOM) < tolerance) {
        // Currently at 1x — stronger zoom on mobile to match common product-page “peek” UX.
        targetZoom = isMobileBreakpoint() ? LOUPE_PEEK_ZOOM : DOUBLE_TAP_ZOOM;
      } else {
        // Currently at 1.5x or any other level, go to 1x
        targetZoom = MIN_ZOOM;
      }
    }

    // If we're not going to 1x, adjust translation to center zoom on the tap point
    if (targetZoom !== MIN_ZOOM) {
      this.#zoomToClientPoint(touch.clientX, touch.clientY, Math.min(MAX_ZOOM, targetZoom));
    } else {
      // Going to 1x, set the scale and center the image
      this.#scale = targetZoom;
      this.#translate = { x: 0, y: 0 }; // Center the image when going to 1x
    }

    this.#requestUpdateTransform();

    if (isMobileBreakpoint()) {
      if (targetZoom !== MIN_ZOOM) {
        this.setAttribute('data-mobile-zoom-active', '');
      } else {
        this.removeAttribute('data-mobile-zoom-active');
      }
    }
  }

  /**
   * Loupe: fixed peek zoom, aim so content under (clientX, clientY) matches zoom from 1x at that point.
   * @param {number} clientX
   * @param {number} clientY
   */
  #aimLoupeAtClientPoint(clientX, clientY) {
    const s = clamp(LOUPE_PEEK_ZOOM, MIN_ZOOM, MAX_ZOOM);
    this.#scale = s;

    if (!this.#loupeBounds.valid) {
      this.#refreshLoupeBounds();
    }
    const { left, top, width, height } = this.#loupeBounds;
    const localX = clientX - left;
    const localY = clientY - top;
    const distanceFromCenter = {
      x: localX - width / 2,
      y: localY - height / 2,
    };

    const scaleDelta = s - 1.0;
    this.#translate.x = -(distanceFromCenter.x * scaleDelta) / s;
    this.#translate.y = -(distanceFromCenter.y * scaleDelta) / s;
  }

  /**
   * Zoom so the image content under the viewport point (clientX/clientY) stays under that point.
   * @param {number} clientX
   * @param {number} clientY
   * @param {number} targetScale
   */
  #zoomToClientPoint(clientX, clientY, targetScale) {
    const oldScale = this.#scale;
    this.#scale = clamp(targetScale, MIN_ZOOM, MAX_ZOOM);
    if (this.#scale === oldScale) return;

    const rect = this.getBoundingClientRect();
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;
    const distanceFromCenter = {
      x: localX - rect.width / 2,
      y: localY - rect.height / 2,
    };

    const scaleDelta = this.#scale / oldScale - 1.0;
    this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;
    this.#loupeBounds.valid = false;
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2 && !isMobileBreakpoint()) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processTwoFingerGesture(touch1, touch2);
      }
    } else if (touchCount >= 1 && this.#isDragging) {
      // On mobile, a second finger down should not cancel one-finger pan.
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
    }
  };

  /**
   * Process zoom gesture from touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processTwoFingerGesture(touch1, touch2) {
    if (!this.#initialDistance || this.#initialDistance <= 0) return;

    // Calculate midpoint directly without object allocation
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    // Pinch zoom factor based on finger distance change
    const currentDistance = getDistance(touch1, touch2);
    const targetScale = clamp((currentDistance / this.#initialDistance) * this.#startScale, MIN_ZOOM, MAX_ZOOM);

    const s0 = this.#startScale;
    const s1 = targetScale;

    // Update scale first; constrainTranslation() will use it.
    this.#scale = s1;

    // Support pan + zoom together:
    // Translation is calculated from:
    // - midpoint movement (dx/dy)
    // - scaling correction based on where the midpoint started relative to container center.
    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;

    const startRelX = this.#startMidpoint.x - containerCenterX;
    const startRelY = this.#startMidpoint.y - containerCenterY;

    const dxRelX = midX - this.#startMidpoint.x;
    const dyRelY = midY - this.#startMidpoint.y;

    // Derived so that the image point under the midpoint stays under the midpoint.
    this.#translate.x =
      this.#startTwoFingerTranslate.x + dxRelX / s1 + startRelX * (1 / s1 - 1 / s0);
    this.#translate.y =
      this.#startTwoFingerTranslate.y + dyRelY / s1 + startRelY * (1 / s1 - 1 / s0);

    // Mark manual zoom when scale actually changes.
    const zoomEps = 0.001;
    this.#hasManualZoom = Math.abs(s1 - s0) > zoomEps;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from touch
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    // Mobile loupe: move finger = move focal point on the unscaled image, not panning a zoomed layer.
    if (isMobileBreakpoint() && this.hasAttribute('data-mobile-zoom-active')) {
      if (!this.#mobileDragAttrApplied) {
        this.#mobileDragAttrApplied = true;
        this.setAttribute('data-mobile-zoom-dragging', '');
      }
      this.#scheduleLoupeAim(touch.clientX, touch.clientY);
      this.#hasDraggedBeyondThreshold = true;
      return;
    }

    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      // Movement is too small, don't process as drag yet
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    // Calculate movement deltas for translation
    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    // Calculate new translation directly
    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#hasDraggedBeyondThreshold = false;

      // Mobile "press to peek" loupe: restore full view when the finger leaves the screen.
      if (isMobileBreakpoint()) {
        this.#cancelLoupeAimRaf();
        this.#mobileDragAttrApplied = false;
        this.removeAttribute('data-mobile-zoom-dragging');
        this.#scale = RESET_ZOOM;
        this.#startScale = RESET_ZOOM;
        this.#translate.x = 0;
        this.#translate.y = 0;
        this.#hasManualZoom = false;
        this.#queueClearMobilePeekChrome();
      }

      this.#requestUpdateTransform();
    }
  };

  /** Clears loupe UI flags, timers, and desktop hover state. */
  #clearLoupeUi() {
    this.#cancelLoupeAimRaf();
    this.#mobileDragAttrApplied = false;
    this.#desktopLoupeMovingAttrApplied = false;
    this.#loupeBounds.valid = false;

    if (this.#mobilePeekUiClearTimer !== null) {
      clearTimeout(this.#mobilePeekUiClearTimer);
      this.#mobilePeekUiClearTimer = null;
    }
    if (this.#desktopLoupeUiClearTimer !== null) {
      clearTimeout(this.#desktopLoupeUiClearTimer);
      this.#desktopLoupeUiClearTimer = null;
    }
    this.#desktopLoupeHovering = false;
    this.removeAttribute('data-mobile-zoom-active');
    this.removeAttribute('data-mobile-zoom-dragging');
    this.removeAttribute('data-desktop-hover-loupe');
    this.removeAttribute('data-desktop-hover-loupe-moving');
  }

  /**
   * After releasing mobile peek, allow CSS transform transition to finish before restoring
   * scroll/touch behaviour on the wrapper.
   */
  #queueClearMobilePeekChrome() {
    if (!isMobileBreakpoint()) return;

    if (this.#mobilePeekUiClearTimer !== null) {
      clearTimeout(this.#mobilePeekUiClearTimer);
      this.#mobilePeekUiClearTimer = null;
    }

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const delay = reducedMotion ? 0 : 260;

    this.#mobilePeekUiClearTimer = setTimeout(() => {
      this.#mobilePeekUiClearTimer = null;
      this.removeAttribute('data-mobile-zoom-active');
    }, delay);
  }

  /**
   * After pointer leaves desktop hover loupe, delay removing the attribute so transform can ease out.
   */
  #queueClearDesktopLoupeChrome() {
    if (isMobileBreakpoint()) return;

    if (this.#desktopLoupeUiClearTimer !== null) {
      clearTimeout(this.#desktopLoupeUiClearTimer);
      this.#desktopLoupeUiClearTimer = null;
    }

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const delay = reducedMotion ? 0 : 240;

    this.#desktopLoupeUiClearTimer = setTimeout(() => {
      this.#desktopLoupeUiClearTimer = null;
      this.removeAttribute('data-desktop-hover-loupe');
    }, delay);
  }

  /**
   * @param {PointerEvent} event
   */
  #handleDesktopLoupePointerEnter = (event) => {
    if (isMobileBreakpoint()) return;
    if (event.pointerType === 'touch') return;
    if (!this.#isZoomDialogOpen()) return;

    this.#cancelLoupeAimRaf();
    this.#desktopLoupeHovering = true;
    this.#desktopLoupeMovingAttrApplied = false;
    this.#refreshLoupeBounds();
    this.setAttribute('data-desktop-hover-loupe', '');
    this.#aimLoupeAtClientPoint(event.clientX, event.clientY);
    this.#requestUpdateTransform();
  };

  /**
   * @param {PointerEvent} event
   */
  #handleDesktopLoupePointerMove = (event) => {
    if (!this.#desktopLoupeHovering || isMobileBreakpoint()) return;
    if (event.pointerType === 'touch') return;

    if (!this.#desktopLoupeMovingAttrApplied) {
      this.#desktopLoupeMovingAttrApplied = true;
      this.setAttribute('data-desktop-hover-loupe-moving', '');
    }
    this.#scheduleLoupeAim(event.clientX, event.clientY);
  };

  /**
   * @param {PointerEvent} event
   */
  #handleDesktopLoupePointerLeave = (event) => {
    if (isMobileBreakpoint()) return;
    if (event.pointerType === 'touch') return;
    if (!this.#desktopLoupeHovering) return;

    this.#cancelLoupeAimRaf();
    this.#desktopLoupeHovering = false;
    this.#desktopLoupeMovingAttrApplied = false;
    this.removeAttribute('data-desktop-hover-loupe-moving');
    this.#scale = RESET_ZOOM;
    this.#startScale = RESET_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;
    this.#requestUpdateTransform();
    this.#queueClearDesktopLoupeChrome();
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    // Narrow viewports: optional mouse/pen drag (primary zoom uses touch handlers).
    if (!isMobileBreakpoint()) return;
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;

    preventDefault(event);

    this.#pointerId = event.pointerId;
    this.#isPointerDragging = true;
    this.#hasPointerDraggedBeyondThreshold = false;

    this.#pointerStartPosition = { x: event.clientX, y: event.clientY };
    this.#pointerStartTranslate = { x: this.#translate.x, y: this.#translate.y };

    // Capture ensures we keep receiving pointer events outside the element.
    try {
      this.setPointerCapture(event.pointerId);
    } catch {
      // Ignore if not supported.
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (!this.#isPointerDragging) return;
    if (event.pointerType === 'touch') return;
    if (this.#pointerId !== event.pointerId) return;

    const dx = event.clientX - this.#pointerStartPosition.x;
    const dy = event.clientY - this.#pointerStartPosition.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (!this.#hasPointerDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      return;
    }
    this.#hasPointerDraggedBeyondThreshold = true;

    // No need to allow panning when at minimum zoom; constraints will clamp.
    preventDefault(event);

    this.#translate.x = this.#pointerStartTranslate.x + dx / this.#scale;
    this.#translate.y = this.#pointerStartTranslate.y + dy / this.#scale;
    this.#requestUpdateTransform();
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (this.#pointerId !== event.pointerId) return;
    this.#isPointerDragging = false;
    this.#pointerId = null;
    this.#hasPointerDraggedBeyondThreshold = false;
    this.#requestUpdateTransform();
  };

  /**
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    if (!this.#isZoomDialogOpen()) return;
    // Desktop uses hover loupe; wheel zoom only on narrow viewports.
    if (!isMobileBreakpoint()) return;

    if (event.cancelable) preventDefault(event);

    const oldScale = this.#scale;
    const zoomIn = event.deltaY < 0;
    const zoomFactor = zoomIn ? 1.12 : 1 / 1.12;
    const newScale = clamp(oldScale * zoomFactor, MIN_ZOOM, MAX_ZOOM);

    if (newScale === oldScale) return;

    const rect = this.getBoundingClientRect();
    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    const distanceFromCenter = {
      x: localX - rect.width / 2,
      y: localY - rect.height / 2,
    };

    const scaleDelta = newScale / oldScale - 1.0;

    this.#scale = newScale;
    this.#hasManualZoom = true;
    this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
  };

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#imgRef) return;

    // Keep scale between MIN_ZOOM (1) and MAX_ZOOM (5)
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    // At minimum zoom (1x), the full image should be visible with no dragging allowed
    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    // Get wrapper dimensions (prefer loupe cache to skip layout read each frame)
    let ww = this.#loupeBounds.width;
    let wh = this.#loupeBounds.height;
    if (!this.#loupeBounds.valid || ww <= 0 || wh <= 0) {
      const r = this.getBoundingClientRect();
      ww = r.width;
      wh = r.height;
    }

    // Calculate ACTUAL image content dimensions at current zoom
    // The image element may fill the wrapper, but the content has its own aspect ratio
    const imageElement = this.#imgRef;
    let naturalWidth, naturalHeight;

    // Try to get natural dimensions
    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      // Fallback: assume square image if we can't get natural dimensions
      naturalWidth = ww;
      naturalHeight = ww;
    }

    // Calculate how the image fits within the wrapper (object-fit: contain behavior).
    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = ww / wh;

    let actualImageWidth, actualImageHeight;

    // object-fit: contain — one axis fills exactly, the other is smaller
    if (imageAspectRatio > wrapperAspectRatio) {
      // Image is wider — width fits exactly, height is smaller (letterboxed top/bottom)
      actualImageWidth = ww;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      // Image is taller — height fits exactly, width is smaller (letterboxed left/right)
      actualImageHeight = wh;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    // Apply current zoom scale
    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;

    // SIMPLE APPROACH: Calculate constraints directly from image content dimensions
    // If image content is larger than wrapper, calculate max translation directly

    const horizontalOverflow = Math.max(0, scaledImageWidth - ww);
    const verticalOverflow = Math.max(0, scaledImageHeight - wh);

    // Max translation is half the overflow (since image starts centered)
    const maxTranslateX = horizontalOverflow / 2 / this.#scale;
    const maxTranslateY = verticalOverflow / 2 / this.#scale;

    // Apply symmetric constraints (object-fit: contain behavior)
    // Image starts centered, can move maxTranslate in each direction
    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
  }

  /** Apply constrained transform + CSS vars in one pass (single layout flush). */
  #flushTransformSync() {
    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;
    this.#flushTransformSync();
  };

  /**
   * Reset zoom state (1x, centered position)
   * Called when zoom is exited/closed
   */
  #resetZoom() {
    this.#clearLoupeUi();
    this.#cancelAnimationFrame();

    // Reset scale and translation to defaults
    this.#scale = RESET_ZOOM;
    this.#startScale = RESET_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;
    this.#hasManualZoom = false;

    // Reset gesture state to prevent interference on next zoom open
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.#isPointerDragging = false;
    this.#pointerId = null;
    this.#hasPointerDraggedBeyondThreshold = false;

    this.#flushTransformSync();

    // If wrapper is scrollable (mobile), ensure we don't carry scroll offsets either.
    this.scrollLeft = 0;
    this.scrollTop = 0;
  }

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
    this.#clearLoupeUi();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch} point1 - First point or touch
 * @param {Point | Touch} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch objects (clientX, clientY)
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
